import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { learningProgressService, LearningProgress } from '../../services/learningProgressService';
import { learningModuleService, LearningModule as ServiceLearningModule } from '../../services/learningModuleService';
import { ActivityRenderer, Activity } from './activities';
import './LessonStudy.css';

interface LessonStudyProps {
  user: User | null;
  onToggleSidebar: () => void;
}

interface LessonFileData {
  lesson_id: string;
  title: string;
  description: string;
  type: string;
  difficulty: string;
  estimatedTime: number;
  activities: Activity[];
}

interface LessonData extends LessonFileData {
  id: string;
  moduleId: string;
  currentActivityIndex: number;
  completed: boolean;
  totalScore?: number;
}

const LessonStudy: React.FC<LessonStudyProps> = ({
  user,
  onToggleSidebar
}) => {
  const { moduleId, lessonId } = useParams<{ moduleId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Progress tracking state
  const [lessonProgress, setLessonProgress] = useState<LearningProgress | null>(null);
  const [activityProgresses, setActivityProgresses] = useState<Map<string, LearningProgress>>(new Map());

  useEffect(() => {
    if (moduleId && lessonId) {
      loadLessonData(moduleId, lessonId);
    }
  }, [moduleId, lessonId]);


  const loadLessonData = async (moduleId: string, lessonId: string) => {
    setLoading(true);
    try {
      // First, get module info from the service using the ID
      const moduleInfo: ServiceLearningModule | null = await learningModuleService.getLearningModuleById(moduleId);
      
      if (!moduleInfo) {
        throw new Error(`Module ${moduleId} not found in service`);
      }

      // Load the specific lesson JSON file using url_alias
      const lessonResponse = await fetch(`/modules/${moduleInfo.url_alias}/${lessonId}.json`);
      if (!lessonResponse.ok) {
        throw new Error(`Failed to load lesson data for ${lessonId}`);
      }
      const lessonFileData: LessonFileData = await lessonResponse.json();

      // Combine the data and set defaults
      const combinedData: LessonData = {
        ...lessonFileData,
        id: lessonId,
        moduleId: moduleId,
        currentActivityIndex: 0,
        completed: false
      };

      setLessonData(combinedData);
      setCurrentActivityIndex(0);
      
      // Initialize progress tracking
      if (user) {
        await initializeProgressTracking(moduleId, lessonId, combinedData);
      }
    } catch (error) {
      console.error('Error loading lesson data:', error);
      setLessonData(null);
    } finally {
      setLoading(false);
    }
  };

  // Initialize progress tracking for lesson and activities
  const initializeProgressTracking = async (moduleId: string, lessonId: string, lessonData: LessonData) => {
    try {
      // Check if lesson progress already exists
      let existingLessonProgress = await learningProgressService.getProgressByModuleAndActivity(moduleId, lessonId);
      
      if (!existingLessonProgress) {
        // Create new lesson progress
        try {
          const result = await learningProgressService.startActivity(moduleId, lessonId, {
            status: 'in_progress',
            completion_percentage: 0,
            attempts: 1,
            metadata: {
              lesson_title: lessonData.title,
              total_activities: lessonData.activities.length
            }
          });
          
          if (result.success && result.progress_id) {
            existingLessonProgress = await learningProgressService.getLearningProgressById(result.progress_id);
          }
        } catch (error: any) {
          console.warn(`Lesson progress might already exist for ${lessonId}, trying to fetch it:`, error);
          // Try to fetch it again in case it was created by another request
          existingLessonProgress = await learningProgressService.getProgressByModuleAndActivity(moduleId, lessonId);
        }
      } else {
        // Update existing progress to mark as resumed
        try {
          await learningProgressService.resumeProgress(existingLessonProgress.progress_id);
          existingLessonProgress = await learningProgressService.getLearningProgressById(existingLessonProgress.progress_id);
        } catch (error) {
          console.warn('Error resuming progress, using existing record:', error);
        }
      }
      
      setLessonProgress(existingLessonProgress);

      // Initialize activity progress tracking
      const activityProgressMap = new Map<string, LearningProgress>();
      
      // Process activities in batches to avoid race conditions
      for (const activity of lessonData.activities) {
        const activityId = `${lessonId}_${activity.id}`;
        
        try {
          let activityProgress = await learningProgressService.getProgressByModuleAndActivity(moduleId, activityId);
          
          if (!activityProgress) {
            // Create activity progress record only if it doesn't exist
            const result = await learningProgressService.createLearningProgress({
              module_id: moduleId,
              activity_id: activityId,
              progress_type: 'activity',
              status: 'not_started',
              completion_percentage: 0,
              attempts: 0,
              metadata: {
                activity_title: activity.title,
                activity_type: activity.type,
                lesson_id: lessonId
              }
            });
            
            if (result.success && result.progress_id) {
              activityProgress = await learningProgressService.getLearningProgressById(result.progress_id);
            } else if (result.message && result.message.includes('existing')) {
              // If creation returned existing record info, fetch it
              activityProgress = await learningProgressService.getProgressByModuleAndActivity(moduleId, activityId);
            }
          }
          
          if (activityProgress) {
            activityProgressMap.set(activity.id, activityProgress);
          }
        } catch (error: any) {
          console.warn(`Error handling activity progress for ${activityId}:`, error);
          // Try to fetch existing progress even if creation failed
          try {
            const existingActivity = await learningProgressService.getProgressByModuleAndActivity(moduleId, activityId);
            if (existingActivity) {
              activityProgressMap.set(activity.id, existingActivity);
            }
          } catch (fetchError) {
            console.error(`Failed to fetch existing activity progress for ${activityId}:`, fetchError);
          }
        }
      }
      
      setActivityProgresses(activityProgressMap);
    } catch (error) {
      console.error('Error initializing progress tracking:', error);
    }
  };


  // Save activity progress
  const saveActivityProgress = async (activityId: string, completed: boolean, score?: number) => {
    const activityProgress = activityProgresses.get(activityId);
    if (!activityProgress) return;
    
    try {
      const updateData = {
        status: completed ? 'completed' as const : 'in_progress' as const,
        completion_percentage: completed ? 100 : 50,
        attempts: (activityProgress.attempts || 0) + 1,
        ...(score !== undefined && { score }),
        metadata: {
          ...activityProgress.metadata,
          completed_at: completed ? new Date().toISOString() : undefined
        }
      };
      
      await learningProgressService.updateLearningProgress(activityProgress.progress_id, updateData);
      
      // Update local state
      const updatedProgress = { ...activityProgress, ...updateData };
      const newMap = new Map(activityProgresses);
      newMap.set(activityId, updatedProgress);
      setActivityProgresses(newMap);
    } catch (error) {
      console.error('Error saving activity progress:', error);
    }
  };

  const handleNextActivity = async () => {
    if (!lessonData) return;
    
    const currentActivity = lessonData.activities[currentActivityIndex];
    
    // Automatically mark current activity as completed when moving to next
    await saveActivityProgress(currentActivity.id, true, 100);
    
    // Mark activity as completed in local state
    const updatedActivities = [...lessonData.activities];
    updatedActivities[currentActivityIndex] = {
      ...updatedActivities[currentActivityIndex],
      completed: true,
      score: 100
    };
    
    setLessonData({
      ...lessonData,
      activities: updatedActivities
    });
    
    if (currentActivityIndex < lessonData.activities.length - 1) {
      setCurrentActivityIndex(currentActivityIndex + 1);
    } else {
      // Lesson completed
      handleLessonComplete();
    }
  };

  const handlePrevActivity = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(currentActivityIndex - 1);
    }
  };

  const handleActivityComplete = async (score?: number) => {
    if (!lessonData) return;
    
    const currentActivity = lessonData.activities[currentActivityIndex];
    
    // Mark current activity as completed
    const updatedActivities = [...lessonData.activities];
    updatedActivities[currentActivityIndex] = {
      ...updatedActivities[currentActivityIndex],
      completed: true,
      score: score
    };
    
    setLessonData({
      ...lessonData,
      activities: updatedActivities
    });
    
    // Save activity progress
    await saveActivityProgress(currentActivity.id, true, score);
  };

  const handleLessonComplete = async () => {
    if (!lessonData || !lessonProgress || !moduleId) return;
    
    try {
      console.log('Lesson completed!');
      
      // Mark lesson as completed
      const completedActivities = lessonData.activities.filter(activity => activity.completed).length;
      const completionPercentage = Math.round((completedActivities / lessonData.activities.length) * 100);
      
      await learningProgressService.markCompleted(lessonProgress.progress_id, {
        completion_percentage: completionPercentage,
        metadata: {
          ...lessonProgress.metadata,
          completed_at: new Date().toISOString(),
          all_activities_completed: true
        }
      });
      
      // Update module progress
      await updateModuleProgress();
      
      console.log('Progress saved successfully!');
      
      // Navigate back to previous page (could be Days page or module page)
      navigate(-1);
    } catch (error) {
      console.error('Error completing lesson:', error);
      // Still navigate back even if saving fails
      navigate(-1);
    }
  };

  // Update overall module progress based on completed lessons
  const updateModuleProgress = async () => {
    if (!moduleId) return;
    
    try {
      // Get current module progress
      const moduleProgress = await learningProgressService.getModuleProgress(moduleId);
      
      // Calculate completion based on activities
      const totalActivities = moduleProgress.activity_progress.length;
      const completedActivities = moduleProgress.activity_progress.filter(
        activity => activity.status === 'completed'
      ).length;
      
      const moduleCompletionPercentage = totalActivities > 0 
        ? Math.round((completedActivities / totalActivities) * 100)
        : 0;
      
      // Update or create module-level progress
      if (moduleProgress.module_progress) {
        await learningProgressService.updateLearningProgress(
          moduleProgress.module_progress.progress_id,
          {
            completion_percentage: moduleCompletionPercentage,
            status: moduleCompletionPercentage === 100 ? 'completed' : 'in_progress'
          }
        );
      } else {
        // Create module progress if it doesn't exist
        await learningProgressService.startModule(moduleId, {
          completion_percentage: moduleCompletionPercentage,
          status: moduleCompletionPercentage === 100 ? 'completed' : 'in_progress'
        });
      }
    } catch (error) {
      console.error('Error updating module progress:', error);
    }
  };

  const handleBackToModule = async () => {
    navigate(-1); // Navigate back to previous page
  };

  const getActivityTypeIcon = (type: string) => {
    switch (type) {
      case 'read': return '📖';
      case 'mcq': return '❓';
      case 'quiz': return '📝';
      case 'game': return '🎮';
      case 'listen': return '🎧';
      case 'write': return '✍️';
      case 'speak': return '🗣️';
      case 'reflection': return '🤔';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div className="lesson-study-container">
        <div className="lesson-study-loading">
          <div className="loading-spinner"></div>
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="lesson-study-container">
        <div className="lesson-study-error">
          <h2>Lesson Not Found</h2>
          <p>The requested lesson could not be found.</p>
          <button className="back-btn" onClick={handleBackToModule}>
            ← Back to Module
          </button>
        </div>
      </div>
    );
  }

  const currentActivity = lessonData.activities[currentActivityIndex];
  const progressPercentage = ((currentActivityIndex + 1) / lessonData.activities.length) * 100;

  return (
    <div className="lesson-study-container">
      <div className="lesson-study-header">
        <div className="lesson-study-header-left">
          <button 
            className="lesson-study-menu-btn"
            onClick={onToggleSidebar}
            title="Toggle sidebar"
          >
            ☰
          </button>
          <button 
            className="lesson-study-back-btn"
            onClick={handleBackToModule}
            title="Back to module"
          >
            ←
          </button>
          <div className="lesson-study-title-section">
            <h1 className="lesson-study-title">{lessonData.title}</h1>
            <p className="lesson-study-subtitle">{lessonData.description}</p>
          </div>
        </div>
      </div>

      <div className="lesson-study-progress-bar">
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="progress-text">
          Activity {currentActivityIndex + 1} of {lessonData.activities.length}
        </div>
      </div>

      <div className="lesson-study-content">
        <div className="activity-header">
          <div className="activity-icon">
            {getActivityTypeIcon(currentActivity.type)}
          </div>
          <h2 className="activity-title">{currentActivity.title}</h2>
        </div>

        <div className="activity-content">
          <ActivityRenderer 
            activity={currentActivity}
            onComplete={handleActivityComplete}
          />
        </div>
      </div>

      <div className="lesson-study-navigation">
        <button 
          className="nav-btn prev-btn"
          onClick={handlePrevActivity}
          disabled={currentActivityIndex === 0}
        >
          ← Previous
        </button>
        
        <div className="activity-indicators">
          {lessonData.activities.map((_, index) => (
            <div
              key={index}
              className={`activity-indicator ${
                index === currentActivityIndex ? 'current' : ''
              } ${
                index < currentActivityIndex ? 'completed' : ''
              }`}
            />
          ))}
        </div>

        <button 
          className="nav-btn next-btn"
          onClick={handleNextActivity}
        >
          {currentActivityIndex === lessonData.activities.length - 1 ? 'Complete Lesson' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default LessonStudy;
